import React, { useRef, useState } from 'react';
import { getProjectLinkConfigByName, ProjectLinkConfig } from 'domain/shared/newProject';
import { useInputHelper, AutosuggestionDropdownLayoutType, Suggestion } from 'framework/form';
import { ChangeEvent, SuggestionSelectedEventData } from 'react-autosuggest';
import { useUiTheme } from 'framework/uiTheme';
import { Stack } from '@mui/material';

interface ProjectToolAutosuggestDropdownProps {
  source: string;
  label?: string;
  suggestions: ProjectLinkConfig[];
  disabled?: boolean;
}

const ProjectToolAutosuggestDropdown = ({
  source,
  suggestions: predefinedSuggestions = [],
  disabled,
  ...props
}: ProjectToolAutosuggestDropdownProps) => {
  const { AutosuggestionDropdownLayout } = useUiTheme<AutosuggestionDropdownLayoutType>();
  const inputHelper = useInputHelper({ source, disabled });
  const onChangeCallback = inputHelper.getRawOnChangeCallback();

  const value = inputHelper.getValue();
  const { Icon, displayName } = getProjectLinkConfigByName(value);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const suggestionsPageRef = useRef(0);

  const onSuggestionsFetchRequested = ({ value: searchValue }: { value: string }) => {
    setSuggestions(
      predefinedSuggestions
        .map(v => ({
          id: v.name,
          text: v.displayName,
        }))
        .filter(v => v.text.toLowerCase().includes(searchValue.toLowerCase())),
    );
    setSuggestionsLoaded(true);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setSuggestionsLoaded(false);
  };

  const onChange = (event: React.FormEvent<HTMLElement>, { newValue }: ChangeEvent) => {
    onChangeCallback?.({ [source]: newValue });
    return newValue;
  };

  const onSuggestionSelected = (
    event: React.FormEvent<any>,
    { suggestion }: SuggestionSelectedEventData<Suggestion>,
  ) => {
    const { text, id, ...otherFields } = suggestion;
    onChangeCallback?.({
      [source]: id,
      ...otherFields,
    });
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Icon />
      <AutosuggestionDropdownLayout
        {...props}
        suggestionsPageRef={suggestionsPageRef}
        suggestions={suggestions}
        suggestionsLoaded={suggestionsLoaded}
        suggestionsTotalPages={0}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        source={source}
        value={displayName || value}
        onChangeCallback={onChange}
        loading={false}
        isRequired={inputHelper.getIsRequired()}
        isVisible={inputHelper.isVisible()}
        errorMessages={inputHelper.getErrorMessages()}
        onFocusCallback={inputHelper.getClearValidationCallback()}
        onBlurCallback={inputHelper.getValidationCallback()}
        noResultMessage=""
        resultsPerPage={predefinedSuggestions.length}
        disabled={inputHelper.getIsDisabled()}
      />
    </Stack>
  );
};

export default ProjectToolAutosuggestDropdown;
